/* global console */

import { traceActivityHandler } from '../activity-handler/trace-activity-handler';
import { AGREEMENTS_FACILITY_CODES_ELEMENT_ID } from '../agreements/agreements.constants';
import { DWT_DEFRA_SUBMISSIONS_FACILITY_CODE_ELEMENT_ID } from '../dwt-defra-submissions/dwt-defra-submissions.constants';
import { DWT_PAYLOADS_FACILITY_CODE_ELEMENT_ID } from '../dwt-payloads/dwt-payloads.constants';
import {
    MATERIAL_SUMMARY_DESTINATION_FACILITY_ELEMENT_ID,
    MATERIAL_SUMMARY_ORIGIN_FACILITY_ELEMENT_ID,
} from '../material-summary/constants/material-summary.constants';
import type { ReportFilterSelectOption } from '../shared/report-filter-select-option.model';
import { facilitiesToSelectOptions } from './utils/facilities-to-select-options.util';
import type { TraceUserInfoApiPort, TraceUserInfoFacilityDto } from './trace-user-info.types';

/**
 * Holds Get-UserInfo-derived state for the add-in. Cleared when the user signs out.
 * Facilities drive the “Facility codes” multiselect on the report filters panel.
 */
export class TraceUserInfoStore {
    private facilityOptions: readonly ReportFilterSelectOption[] = [];
    private facilitiesLoaded = false;
    private loadPromise: Promise<void> | null = null;

    constructor(private readonly userInfoApi: TraceUserInfoApiPort) {}

    clear(): void {
        this.facilityOptions = [];
        this.facilitiesLoaded = false;
        this.loadPromise = null;
    }

    getFacilityCodeOptions(): readonly ReportFilterSelectOption[] {
        return this.facilityOptions;
    }

    /** Merged into reporting-target panel rendering so definitions can keep static placeholders. */
    getDynamicFilterOptions(): Readonly<Record<string, readonly ReportFilterSelectOption[]>> {
        return {
            [AGREEMENTS_FACILITY_CODES_ELEMENT_ID]: this.facilityOptions,
            [DWT_PAYLOADS_FACILITY_CODE_ELEMENT_ID]: this.facilityOptions,
            [DWT_DEFRA_SUBMISSIONS_FACILITY_CODE_ELEMENT_ID]: this.facilityOptions,
            [MATERIAL_SUMMARY_ORIGIN_FACILITY_ELEMENT_ID]: this.facilityOptions,
            [MATERIAL_SUMMARY_DESTINATION_FACILITY_ELEMENT_ID]: this.facilityOptions,
        };
    }

    /**
     * Loads allowed facilities from Get-UserInfo. Idempotent per session until {@link clear}.
     */
    async ensureFacilitiesLoaded(accessToken: string): Promise<void> {
        if (this.facilitiesLoaded) {
            return;
        }
        if (this.loadPromise) {
            return this.loadPromise;
        }
        this.loadPromise = this.loadFacilitiesInternal(accessToken).finally(() => {
            this.loadPromise = null;
        });
        return this.loadPromise;
    }

    private async loadFacilitiesInternal(accessToken: string): Promise<void> {
        const result = await this.userInfoApi.fetchGetUserInfo(accessToken);
        if (result.kind === 'failure') {
            console.error('Get-UserInfo failed', result.message);
            this.facilityOptions = [];
            this.facilitiesLoaded = true;
            return;
        }

        const raw = result.data.facilities;
        const list: TraceUserInfoFacilityDto[] = Array.isArray(raw)
            ? raw.filter(
                  (row): row is TraceUserInfoFacilityDto => row !== null && typeof row === 'object'
              )
            : [];

        this.facilityOptions = facilitiesToSelectOptions(list);
        this.facilitiesLoaded = true;
    }
}

export const traceUserInfoStore = new TraceUserInfoStore(traceActivityHandler);
